import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Bot, BookOpen, Workflow, ShieldCheck, Loader2,
  CheckCircle2, AlertTriangle, Phone, ArrowRight,
} from 'lucide-react';
import KnowledgeBasePage from './KnowledgeBasePage';
import TestAvaButton from './TestAvaButton';
import BrainVersionsCard from './BrainVersionsCard';

/**
 * Ava Brain — unified single dashboard for everything that drives Ava.
 *
 * Four sub-tabs:
 *   1. Identity   — name, voice, tone, languages, locked NextBell greeting preview
 *   2. Knowledge  — full KB management (delegated to <KnowledgeBasePage />)
 *   3. Playbook   — overview of the active during-call flow + jump to editor
 *   4. Guardrails — limits the engine enforces (read-only display + a few toggles)
 *
 * Goal: a customer never has to wonder "where do I go to change X about Ava?"
 *       The answer is always: /dashboard/ava
 */
export default function AvaBrainPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState(() => {
    const u = new URLSearchParams(location.search).get('tab');
    return ['identity', 'knowledge', 'playbook'].includes(u) ? u : 'identity';
  });

  const switchTab = (t) => {
    setTab(t);
    navigate(`/dashboard/ava?tab=${t}`, { replace: true });
  };

  const TABS = [
    { id: 'identity',   label: 'Identity',   icon: Bot,        color: 'text-purple-500' },
    { id: 'knowledge',  label: 'Knowledge',  icon: BookOpen,   color: 'text-blue-500' },
    { id: 'playbook',   label: 'Playbook',   icon: Workflow,   color: 'text-blue-500' },
  ];

  return (
    <div data-testid="ava-brain-page" className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center shadow-lg shadow-purple-500/20">
              <Bot className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-xl font-heading font-semibold text-gray-900 dark:text-white">Ava</h1>
            <span className="text-[11px] uppercase tracking-wide font-medium bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/30 px-2 py-0.5 rounded-full">Live 24/7</span>
          </div>
          <p className="text-sm text-gray-500 dark:text-slate-400 max-w-2xl">
            One screen, one source of truth for everything Ava knows and does. Identity, Knowledge, and Playbook. Edits here are reflected on the next call.
          </p>
        </div>
        <TestAvaButton api={api} />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 border-b border-gray-200 dark:border-white/[0.06] overflow-x-auto">
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              data-testid={`ava-tab-${t.id}`}
              onClick={() => switchTab(t.id)}
              className={`flex items-center gap-2 text-sm px-4 py-2.5 -mb-px border-b-2 transition-colors whitespace-nowrap ${active
                ? 'border-purple-500 text-purple-600 dark:text-purple-300 font-medium'
                : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-800 dark:hover:text-white'}`}
            >
              <t.icon className={`w-4 h-4 ${active ? t.color : ''}`} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Body */}
      {tab === 'identity'   && <IdentityTab api={api} user={user} />}
      {tab === 'knowledge'  && <div className="-mx-4 sm:-mx-6 -mt-2"><KnowledgeBasePage /></div>}
      {tab === 'playbook'   && <PlaybookTab api={api} />}
    </div>
  );
}

/* -------------------- Identity tab -------------------- */
function IdentityTab({ api, user }) {
  const [biz, setBiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiName, setAiName] = useState('Ava');
  const [tone, setTone] = useState('warm');
  const [extra, setExtra] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/business/me');
        setBiz(data);
        setAiName(data?.ai_agent_name || data?.ai_name || 'Ava');
        setTone(data?.tone || 'warm');
        setExtra(data?.system_prompt || data?.extra_system_prompt || '');
      } catch (e) {
        const detail = e.response?.data?.detail;
        if (e.response?.status === 400 && /business/i.test(detail || '')) {
          // mid-onboarding — leave empty
        } else {
          toast.error(detail || 'Could not load business profile.');
        }
      } finally { setLoading(false); }
    })();
  }, [api]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/business/me', { ai_agent_name: aiName, tone, system_prompt: extra });
      toast.success('Identity updated. Ava will use it on the next call.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save.');
    } finally { setSaving(false); }
  };

  if (loading) return <Spinner />;

  const greetingPreview = `Hello and thank you for calling ${biz?.name || '[business name]'}. This is ${aiName}, powered by NextBell A.I. Please be advised this call may be recorded for quality assurance and training purposes. How may I address your call today?`;

  return (
    <div className="space-y-5">
      {/* Locked greeting card */}
      <Card title="Branded greeting" subtitle="Locked. Every call opens with this verbatim.">
        <div className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-500/10 dark:to-blue-500/10 border border-purple-200 dark:border-purple-500/30 rounded-lg p-4">
          <p className="text-[13.5px] text-gray-800 dark:text-slate-100 leading-relaxed italic">
            "{greetingPreview}"
          </p>
          <div className="flex items-center gap-1.5 mt-2.5 text-[11px] text-purple-700 dark:text-purple-300">
            <ShieldCheck className="w-3 h-3" />
            <span>NextBell brand contract — cannot be edited</span>
          </div>
        </div>
      </Card>

      {/* Configurable identity */}
      <Card title="Voice and tone" subtitle="How Ava sounds on the phone.">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="AI name (non-greeting use)">
            <input data-testid="ava-name-input" value={aiName} onChange={(e) => setAiName(e.target.value)}
              className="w-full bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08] focus:border-purple-500 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none" />
          </Field>
          <Field label="Tone">
            <select data-testid="ava-tone-select" value={tone} onChange={(e) => setTone(e.target.value)}
              className="w-full bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08] focus:border-purple-500 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none">
              <option value="warm">Warm</option>
              <option value="professional">Professional</option>
              <option value="luxury">Luxury</option>
              <option value="casual">Casual</option>
            </select>
          </Field>
        </div>
        <Field label="Special instructions for Ava (optional)">
          <textarea data-testid="ava-extra-textarea" value={extra} onChange={(e) => setExtra(e.target.value)} rows={3}
            placeholder="e.g. Always offer the Tuesday senior discount. Mention same-day appointments."
            className="w-full bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08] focus:border-purple-500 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none resize-none" />
        </Field>
        <div className="flex justify-end">
          <button data-testid="ava-save-btn" onClick={save} disabled={saving}
            className="text-sm font-medium text-white bg-purple-600 hover:bg-purple-500 disabled:opacity-50 rounded-lg px-4 py-2 inline-flex items-center gap-1.5">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Save changes
          </button>
        </div>
      </Card>
    </div>
  );
}

/* -------------------- Playbook tab -------------------- */
function PlaybookTab({ api }) {
  const [flow, setFlow] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/flows');
        const items = data?.flows || data || [];
        const playbook = items.find((f) => {
          const t = (f.nodes || []).find((n) => n?.data?.kind === 'trigger');
          return t && t?.data?.config?.trigger_type === 'call.answered' && f.active;
        });
        setFlow(playbook || null);
      } catch (e) {
        // Mid-onboarding businesses get a 400; ignore quietly.
      } finally { setLoading(false); }
    })();
  }, [api]);

  if (loading) return <Spinner />;

  if (!flow) {
    return (
      <div className="space-y-5">
        <Card title="No live playbook" subtitle="Ava is running on her base prompt only.">
          <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-lg p-4 flex items-start gap-2.5">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-gray-800 dark:text-slate-100 font-medium">No active during-call playbook found.</p>
              <p className="text-[12.5px] text-gray-600 dark:text-slate-300 mt-0.5">
                Ava keeps working on her base prompt. A default playbook is normally seeded automatically during onboarding, so contact support if you expected one here.
              </p>
            </div>
          </div>
        </Card>
        <BrainVersionsCard api={api} />
      </div>
    );
  }

  const nodeCount = (flow.nodes || []).length;
  const kbNodes = (flow.nodes || []).filter((n) => n?.data?.kind === 'convo_lookup_kb');
  const branches = (flow.nodes || []).filter((n) => n?.data?.kind === 'convo_branch');

  return (
    <div className="space-y-5">
      <Card title={flow.name || 'Active Playbook'} subtitle={flow.is_default_seed ? 'Auto-generated default. Edit any time.' : 'Custom playbook'}>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <Stat icon={Workflow} label="Nodes" value={nodeCount} />
          <Stat icon={ArrowRight} label="Branches" value={branches.length} />
          <Stat icon={BookOpen} label="KB Lookups" value={kbNodes.length} />
        </div>
      </Card>

      <Card title="What happens on every call" subtitle="The execution order Ava follows once she's done greeting.">
        <ol className="text-[13px] text-gray-700 dark:text-slate-200 space-y-2 list-decimal list-inside">
          <li>Ava reads the locked branded greeting.</li>
          <li>She calls <code className="text-[11.5px] bg-gray-100 dark:bg-white/[0.05] px-1 py-0.5 rounded">route_intent</code> to ask the engine what's next.</li>
          <li>The engine walks your playbook and tells her exactly what to say or ask.</li>
          <li>If a node points to the Knowledge Base, the engine pulls the best answer and she reads it.</li>
          <li>The loop continues until the engine says "wrap up". Then Ava ends the call politely.</li>
        </ol>
      </Card>

      <BrainVersionsCard api={api} />
    </div>
  );
}

/* -------------------- Helpers -------------------- */
function Card({ title, subtitle, children }) {
  return (
    <div className="bg-white dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.06] rounded-xl p-5">
      <h3 className="text-[15px] font-heading font-semibold text-gray-900 dark:text-white">{title}</h3>
      {subtitle && <p className="text-[12.5px] text-gray-500 dark:text-slate-400 mt-0.5 mb-4">{subtitle}</p>}
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wide text-gray-500 dark:text-slate-400 font-medium mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] rounded-lg p-3 text-center">
      <Icon className="w-4 h-4 text-gray-400 dark:text-slate-500 mx-auto mb-1" />
      <p className="text-lg font-semibold tabular-nums text-gray-900 dark:text-white">{value}</p>
      <p className="text-[10.5px] uppercase tracking-wide text-gray-500 dark:text-slate-400">{label}</p>
    </div>
  );
}

function Spinner() {
  return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 text-gray-400 animate-spin" /></div>;
}
